// Bulk-import submodal for Manufacturing → Models (upsert correction flow).
import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  inputClass,
  mfgPrimaryBtnLgClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  buildRecipeBulkImportPreview,
  type RecipeBulkImportPreview,
} from "@/lib/manufacturing/recipeBulkImportPreview";
import {
  EXAMPLE_RECIPE_BULK_IMPORT_CSV,
  EXAMPLE_RECIPE_BULK_IMPORT_JSON,
} from "@/lib/manufacturing/recipeBulkImportExample";
import { downloadCsv } from "@/lib/manufacturing/inventoryCsvExport";
import { listManufacturingMaterialUses } from "@/lib/manufacturing/materialUsesClient";
import {
  bulkCreateManufacturingRecipes,
  confirmManufacturingRecipesReview,
  type MfgMaterial,
  type MfgMaterialUse,
  type MfgRecipeBulkCreateResponse,
  type MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import { ManufacturingRecipeImportPreview } from "./ManufacturingRecipeImportPreview";
import { ManufacturingModelsBulkImportResults } from "./ManufacturingModelsBulkImportResults";

const IMPORT_MODE = "upsert" as const;

export function ManufacturingModelsBulkImportModal({
  existingMaterials,
  existingRecipes,
  onClose,
  onImported,
  onPickRecipe,
}: {
  existingMaterials: ReadonlyArray<MfgMaterial>;
  existingRecipes: ReadonlyArray<MfgRecipeSummary>;
  onClose: () => void;
  onImported: (summary: MfgRecipeBulkCreateResponse["summary"]) => void;
  onPickRecipe?: (recipeId: string) => void;
}) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [replaceAck, setReplaceAck] = useState(false);
  const [resultSummary, setResultSummary] = useState<
    MfgRecipeBulkCreateResponse["summary"] | null
  >(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [existingUses, setExistingUses] = useState<MfgMaterialUse[]>([]);

  useEffect(() => {
    void (async () => {
      const r = await listManufacturingMaterialUses({ active_only: true });
      if (r.ok) setExistingUses(r.data.uses);
    })();
  }, []);

  const preview = useMemo<RecipeBulkImportPreview>(
    () =>
      buildRecipeBulkImportPreview(
        text,
        existingMaterials,
        existingRecipes,
        IMPORT_MODE,
        existingUses,
      ),
    [text, existingMaterials, existingRecipes, existingUses],
  );

  async function handleFile(file: File) {
    setText(await file.text());
    setReplaceAck(false);
    setResultSummary(null);
  }

  async function handleSubmit() {
    if (preview.kind !== "ok") return;
    const importable = preview.recipes.filter(
      (r) => r.conflict !== "code_exists" && !r.hasUnresolvedRefs,
    );
    if (importable.length === 0) return;
    const willReplace = importable.some((r) => r.conflict === "will_replace");
    if (willReplace && !replaceAck) return;

    setSubmitting(true);
    setSubmitError(null);
    const payload = importable.map((r) => ({
      code: r.code,
      name: r.name,
      kind: r.kind,
      tags: r.tags,
      notes: r.notes,
      lines: r.lines.map((l) => ({
        line_kind: l.line_kind,
        material_name: l.material_name || undefined,
        component_recipe_code: l.component_recipe_code || undefined,
        material_use_code: l.material_use_code || undefined,
        quantity: l.quantity,
        role: l.role,
        slot_label: l.slot_label,
        upgrade_price_delta: l.upgrade_price_delta,
        swappable: l.swappable,
      })),
    }));
    const result = await bulkCreateManufacturingRecipes({
      recipes: payload,
      mode: IMPORT_MODE,
    });
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.error || "Bulk import failed");
      return;
    }
    setResultSummary(result.data.summary);
    onImported(result.data.summary);
  }

  async function handleConfirmEligible() {
    if (!resultSummary) return;
    const ids = [...resultSummary.created, ...resultSummary.updated]
      .filter((r) => r.lines_pending === 0)
      .map((r) => r.recipe.id);
    if (ids.length === 0) return;
    setConfirming(true);
    setConfirmError(null);
    const result = await confirmManufacturingRecipesReview({ recipe_ids: ids });
    setConfirming(false);
    if (!result.ok) {
      setConfirmError(result.error || "Confirm failed");
      return;
    }
    if (result.data.blocked.length > 0) {
      setConfirmError(
        `${result.data.blocked.length} blocked (pending materials or missing). Confirmed ${result.data.confirmed.length}.`,
      );
    }
    onImported(resultSummary);
    onClose();
  }

  const importableCount =
    preview.kind === "ok"
      ? preview.recipes.filter((r) => r.conflict !== "code_exists" && !r.hasUnresolvedRefs)
          .length
      : 0;
  const replaceCount =
    preview.kind === "ok" ? preview.totals.recipesWillReplace : 0;
  const pendingLineCount =
    preview.kind === "ok" ? preview.totals.linesPending : 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={resultSummary ? undefined : onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          Bulk upload models (CSV / JSON)
        </h3>

        {resultSummary ? (
          <div className="mt-4">
            <ManufacturingModelsBulkImportResults
              summary={resultSummary}
              confirming={confirming}
              confirmError={confirmError}
              onConfirmAllEligible={() => void handleConfirmEligible()}
              onPickRecipe={onPickRecipe}
              onDone={onClose}
            />
          </div>
        ) : (
          <>
            <details className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-300">
              <summary className="cursor-pointer font-medium">Format guide (CSV or JSON)</summary>
              <div className="mt-2 space-y-2">
                <p>
                  Round-trip export uses the same columns, including{" "}
                  <code>line_kind</code> (<code>material</code> / <code>component</code> /{" "}
                  <code>use</code>), sub-recipe codes, and part-use codes. Matching materials and
                  existing sub-recipes / part uses import cleanly; unmatched materials become
                  pending reconcile lines.
                </p>
                <p>
                  Every upload is flagged needs review until confirmed (pending materials must be
                  cleared first). Unknown sub-recipe or part-use codes block that recipe.
                </p>
                <button
                  type="button"
                  className="text-indigo-700 underline hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
                  onClick={() =>
                    downloadCsv("mfg-recipes-import-example.csv", EXAMPLE_RECIPE_BULK_IMPORT_CSV)
                  }
                >
                  Download example CSV
                </button>
                <pre className="overflow-x-auto rounded bg-white p-2 text-[11px] dark:bg-zinc-950">
                  {EXAMPLE_RECIPE_BULK_IMPORT_JSON}
                </pre>
              </div>
            </details>

            <div className="mt-4 space-y-3">
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                Load file (.csv, .json, .txt)
                <input
                  type="file"
                  accept=".csv,.json,.txt,text/csv,application/json,text/plain"
                  className="mt-1 block w-full text-xs text-gray-700 file:mr-2 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100 dark:text-zinc-300"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleFile(f);
                  }}
                />
              </label>
              <label className="block text-xs text-gray-600 dark:text-zinc-300">
                …or paste CSV / JSON
                <textarea
                  className={`${inputClass} mt-1 h-40 font-mono text-[11px]`}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setReplaceAck(false);
                  }}
                  placeholder="Paste CSV with a header row, or JSON like the example above"
                />
              </label>
            </div>

            {preview.kind !== "empty" ? (
              <div className="mt-4">
                <SectionCard title="Preview">
                  <ManufacturingRecipeImportPreview preview={preview} />
                </SectionCard>
              </div>
            ) : null}

            {replaceCount > 0 ? (
              <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={replaceAck}
                  onChange={(e) => setReplaceAck(e.target.checked)}
                />
                <span>
                  I understand <strong>{replaceCount}</strong> existing model
                  {replaceCount === 1 ? "" : "s"} will have BOM lines{" "}
                  <strong>replaced</strong>
                  {pendingLineCount > 0
                    ? ` and ${pendingLineCount} unmatched material name${pendingLineCount === 1 ? "" : "s"} will not populate`
                    : ""}
                  . Nested component/use lines not in this file will be removed.
                </span>
              </label>
            ) : null}

            {submitError ? (
              <div className="mt-3 text-xs text-red-600 dark:text-red-400">{submitError}</div>
            ) : null}

            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className={mfgSecondaryBtnClass} onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className={mfgPrimaryBtnLgClass}
                disabled={
                  submitting ||
                  importableCount === 0 ||
                  (replaceCount > 0 && !replaceAck)
                }
                onClick={() => void handleSubmit()}
              >
                {submitting
                  ? "Uploading…"
                  : `Upload ${importableCount} recipe${importableCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
